/**
 * @fileoverview A raw HTML page, rendered inside a frame that can do nothing.
 *
 * WHY AN IFRAME AND NOT THE SANITISER. Markdown goes through a renderer and
 * then DOMPurify, which strips scripts and event handlers and leaves prose. A
 * raw HTML page is the escape hatch for people who want control of their own
 * markup, so sanitising it into safety would defeat the point of offering it.
 * The other direction -- inserting a stranger's HTML into our document -- would
 * hand them our origin: our cookies, our storage, our relay connection, and the
 * ability to rewrite the page around them. So the HTML is given a document of
 * its own with every capability removed.
 *
 * `sandbox=""` IS THE EMPTY STRING ON PURPOSE, and every token left out of it
 * is a decision:
 *
 *   no allow-scripts         nothing in the page executes, at all
 *   no allow-same-origin     it gets an opaque origin, so even if something did
 *                            run it could not touch our storage or our DOM
 *   no allow-forms           no submitting a viewer's input anywhere
 *   no allow-popups          no opening windows over the reader
 *   no allow-top-navigation  it cannot navigate the tab out from under the
 *                            reader, which is the one that turns a page into a
 *                            phishing redirect
 *   no allow-modals          no alert loops a reader cannot escape
 *
 * ADDING allow-scripts WOULD BE A DESIGN CHANGE, NOT A TWEAK, and it needs an
 * argument made to the orchestrator first. Note that `allow-scripts` together
 * with `allow-same-origin` is the specific pair that lets the framed document
 * remove its own sandbox attribute, so those two must never appear together
 * whatever else is decided.
 *
 * THE BYTES WERE ALREADY VERIFIED BEFORE THEY GOT HERE. The blob is fetched and
 * hashed against the `x` tag in the index event, exactly as a markdown blob is,
 * so a hostile mirror cannot substitute different HTML. The sandbox protects
 * the reader from the AUTHOR; the hash protects them from everybody else.
 *
 * `srcdoc` RATHER THAN A BLOB URL. A blob: URL would inherit our origin, which
 * is the thing the sandbox exists to deny, and it would need revoking. srcdoc
 * hands the markup over directly and the sandbox governs it.
 *
 * THE HEIGHT IS FIXED AND THE FRAME SCROLLS. A frame can only size itself to
 * its content by asking the document inside how tall it is, and an opaque-origin
 * document cannot be asked. Scripts inside could post a message out, but there
 * are no scripts, deliberately. A tall frame with its own scrollbar is the
 * honest consequence of that trade, and the trade is the right way round.
 */

export function SandboxedHtml({ html, title }: { html: string; title: string }) {
  return (
    <div className="page-html-frame">
      <iframe
        className="page-html"
        sandbox=""
        srcDoc={html}
        referrerPolicy="no-referrer"
        // Named for screen readers, which otherwise announce an unlabelled
        // frame and give the reader no idea what is inside it.
        title={`${title} (raw HTML page)`}
      />
      <p className="page-html-note">
        This page is raw HTML, shown in a sandbox. Nothing in it can run scripts, read your
        session, or navigate you away.
      </p>
    </div>
  );
}
